import { Button, Grid, LinearProgress, Typography } from "@mui/material";
import {
  BulkActionType,
  IAircraft,
  IBulkEventPayload,
  IBulkIdsMap,
  IFlight,
  IGroundEvent,
  IImportFlight,
  IModifiedFieldMap,
  IModifiedFlight,
} from "@vmosolutions/oc-interfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { IRootState } from "src/redux/reducers/CombinedReducers";
// Components
import _ from "lodash";
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
import { setAuditFromId } from "src/redux/actions/Audit/Audit.Actions";
import { bulkEvent, chunkPayload } from "src/redux/actions/flights/FlightData.Actions";
import { setFocusedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { IAuditChange, newAuditChange } from "src/redux/reducers/AuditReducer";
import { v1 } from "uuid";

// Filters
// Form fields
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";

// Airport options
import { Theme } from "@mui/material/styles";
import moment from "moment-timezone";
import { IAirportOption } from "src/redux/reducers/StructuresReducer"; // IAirportOptionMap
import { updateAirportOptions } from "src/utils/airportHelper";

interface IAuditEventsCheckProps {
  fileName: string;
  cancelAudit: () => void;
  auditId: string | null;
  windowId: string;
  path: string[];
  loadingAudit: boolean;
  setLoadingAudit: (isLoading: boolean) => void;
}

export enum AuditTableState {
  ALL = "ALL",
  NEW = "NEW",
  MODIFIED = "MODIFIED",
  DELETED = "DELETED",
}

export interface IAuditTableRow {
  id: string;
  state: AuditTableState;
  ocFlight?: IImportFlight;
  importedFlight?: IImportFlight;
  modifiedFields?: IModifiedFieldMap;
}

interface IStateOption {
  label: string;
  value: string;
}
const auditTableRowOptions: IStateOption[] = Object.values(AuditTableState).map((event) => ({
  label: event,
  value: event,
}));

function isNonEmptyArrayOfNumbers(value: any): boolean {
  return Array.isArray(value) && value.length >= 0 && value.every((item) => typeof item === "number");
}

const AuditEventCheckStyleContainer = (theme: Theme): any => ({
  "& .loading_bar": {
    margin: "10px 0px",
  },
  "& .audit_header": {
    margin: "0px 5px 15px 5px",
  },
  "& .filter_by_state": {
    "& .input_control": {
      "& label": { width: "auto" },
    },
  },
  "& .table_container": {
    marginTop: "20px",
    backgroundColor: theme.custom.timeline.contextListBackground,
    "& .table_column_headers": {
      marginTop: "15px",
      justifyContent: "space-between",
      paddingLeft: "45px",
      paddingRight: "20px",

      "& .filter_container": {
        alignItems: "flex-start",
        marginBottom: "20px",
        "& .flight_filter": {
          width: "auto",
          flexWrap: "wrap",
          marginLeft: "10px",
          paddingLeft: "10px",
          borderLeft: `2px solid ${theme.custom.forms.inputBorder}`,
          "& .range_datetimefield": {
            width: "auto",
            "& .input_control label": { width: "auto" },
            "& .input_control .react-datepicker-wrapper": {
              margin: "0px",
              "& input": { width: "90px" },
            },
          },
          "& .flightnumber_textfield": {
            "& .input_control label": { width: "auto" },
            "& .input-base": { width: "50px" },
          },
          "& .origin_selectfield, & .destination_selectfield, & .aircraft_selectfield": {
            "& .input_control label": { width: "auto" },
          },
        },
      },

      "& .ocLoaded": {
        height: "15px",
        width: "318px",
        borderRight: `1px solid ${theme.palette.primary.main}`,
        borderLeft: `1px solid ${theme.palette.primary.main}`,
        borderTop: `1px solid ${theme.palette.primary.main}`,
        textAlign: "center",
      },
      "& .fileImport": {
        height: "15px",
        width: "340px",
        borderRight: `1px solid ${theme.palette.primary.main}`,
        borderLeft: `1px solid ${theme.palette.primary.main}`,
        borderTop: `1px solid ${theme.palette.primary.main}`,
        textAlign: "center",
      },
      "& .column_header_title": {
        backgroundColor: theme.custom.timeline.contextListBackground,
        color: theme.palette.primary.main,
        padding: "2px 8px",
        marginTop: "-12px",
      },
    },
  },
});

const auditTableInfo: ITableColumn[] = [
  // ORIGINAL FLIGHT
  {
    id: "ocFlight[flightNumber]",
    value: "ocFlight[flightNumber]",
    index: 0,
    numeric: true,
    label: "Flt #",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "ocFlight[aircraft][registration]",
    value: "ocFlight[aircraft][registration]",
    index: 1,
    numeric: false,
    label: "Aircraft",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "ocFlight[origin]",
    value: "ocFlight[origin]",
    index: 2,
    numeric: false,
    label: "Origin",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "ocFlight[destination]",
    value: "ocFlight[destination]",
    index: 3,
    numeric: false,
    label: "Dest.",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "ocFlight[actualOut]",
    value: "ocFlight[actualOut]",
    index: 4,
    numeric: false,
    label: "Departure Time UTC",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "ocFlight[actualIn]",
    value: "ocFlight[actualIn]",
    index: 5,
    numeric: false,
    label: "Arrival Time UTC",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },

  {
    id: "state",
    value: "state",
    index: 6,
    numeric: true,
    label: "Event State",
    type: "string",
    width: 1,
    visible: true,
    filter: false,
  },

  //IMPORTED
  {
    id: "importedFlight[flightNumber]",
    value: "importedFlight[flightNumber]",
    index: 7,
    numeric: true,
    label: "Flt #",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "importedFlight[aircraft][registration]",
    value: "importedFlight[aircraft][registration]",
    index: 8,
    numeric: false,
    label: "Aircraft",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "importedFlight[origin]",
    value: "importedFlight[origin]",
    index: 9,
    numeric: false,
    label: "Origin",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "importedFlight[destination]",
    value: "importedFlight[destination]",
    index: 10,
    numeric: false,
    label: "Dest.",
    type: "string",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "importedFlight[actualOut]",
    value: "importedFlight[actualOut]",
    index: 11,
    numeric: false,
    label: "Departure Time UTC",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
  {
    id: "importedFlight[actualIn]",
    value: "importedFlight[actualIn]",
    index: 12,
    numeric: false,
    label: "Arrival Time UTC",
    type: "date",
    width: 10,
    visible: true,
    filter: true,
  },
];

/**
 * Function returned pairing flight status with applied filters.
 * @param rows - table rows
 * @param state - row state "NEW" | "MODIFIED" | "DELETED";
 * @param flightNumber - flight number filter
 * @param aircraft - flight aircraft
 * @param origin - origin filter
 * @param destination - destination filter
 * @param start - start range
 * @param end - end range
 */
const filterTableRows = (
  auditRows: IAuditTableRow[],
  state: IStateOption,
  flightNumber: number | null,
  aircraft: IAircraft | null,
  origin: IAirportOption | null,
  destination: IAirportOption | null,
  start: number | null,
  end: number | null
): IAuditTableRow[] => {
  const filteredRows = auditRows.filter((row: IAuditTableRow) => {
    if (!row) {
      return false;
    }
    const fl = row?.importedFlight;
    const filters: boolean[] = [];
    filters.push(state ? state.value === AuditTableState.ALL || row.state === state.value : true);
    if (fl) {
      filters.push(flightNumber ? String(fl?.flightNumber ?? "").includes(String(flightNumber)) : true);
      filters.push(aircraft ? fl?.aircraft?.registration === aircraft?.registration : true);
      filters.push(origin ? fl?.origin === origin.ident : true);
      filters.push(destination ? fl?.destination === destination.ident : true);
      filters.push(start ? fl?.actualOut > start : true);
      filters.push(end ? fl?.actualOut < end : true);
    }
    return filters.every(Boolean);
  });
  return filteredRows;
};

export const bulkBatchSize = 500;
export const AuditEventsCheck = (props: IAuditEventsCheckProps): JSX.Element => {
  const { fileName, cancelAudit, auditId, windowId, path, loadingAudit, setLoadingAudit } = props;
  const dispatch = useDispatch();
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);
  const aircraftOptions = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);

  const [selectedAuditChanges, setSelectedAuditChanges] = useState<IAuditChange>(newAuditChange());
  const [watchingBulkId, setWatchingBulkId] = useState<string | null>(null);

  const audit = useSelector((state: IRootState) => state.audit.auditsWatching[auditId]);
  const bulkOps = useSelector((state: IRootState) => state.bulkBatch);

  // Table Display Filters - returns results that filters
  const [state, setState] = useState<IStateOption>(auditTableRowOptions[0]);
  const [flightNumber, setFlightNumber] = useState<string | null>(null);
  const [start, setStart] = useState<number | null>(null);
  const [end, setEnd] = useState<number | null>(null);
  const [aircraft, setAircraft] = useState<IAircraft | null>(null);
  const [origin, setOrigin] = useState<IAirportOption | null>(null);
  const [destination, setDestination] = useState<IAirportOption | null>(null);

  const auditBulkEvent = useMemo(() => {
    return watchingBulkId ? bulkOps[watchingBulkId] : null;
  }, [bulkOps, watchingBulkId]);

  const totalSelectedChanges: number = useMemo(() => {
    if (!selectedAuditChanges) {
      return 0;
    }
    const newCount = selectedAuditChanges?.newEvents?.length ?? 0;
    const modifiedCount = selectedAuditChanges?.modifiedEvents?.length ?? 0;
    const deletedCount = selectedAuditChanges?.deletedEvents?.length ?? 0;
    return newCount + modifiedCount + deletedCount;
  }, [selectedAuditChanges]);

  const bulkProgress: number = useMemo(() => {
    if (!auditBulkEvent) {
      return 0;
    }
    const totalCount = auditBulkEvent.records;
    const completed = auditBulkEvent?.successfulEventIds?.length ?? 0;
    const failed = auditBulkEvent?.failedEvents?.length ?? 0;
    const percentageComplete = totalCount === 0 ? 0 : ((completed + failed) * 100) / totalCount;
    return percentageComplete;
  }, [auditBulkEvent]);

  const bulkProcessActive = bulkProgress > 0 && bulkProgress < 100;

  const statusMessage: string = useMemo(() => {
    if (!auditBulkEvent || totalSelectedChanges === 0) {
      return "";
    }
    //const completed = auditBulkEvent?.successfulEventIds?.length ?? 0;
    //const failed = auditBulkEvent?.failedEvents?.length ?? 0;
    //return `${completed} successful and ${failed} failed events out of ${totalSelectedChanges} selected for import.`;
    const { records, successfulEventIds, failedEvents } = auditBulkEvent;
    const failedUpdates =
      Array.isArray(failedEvents) && failedEvents.length > 0 ? `, ${failedEvents?.length} changes failed` : "";
    return `${successfulEventIds?.length} of ${records} changes successfully imported${failedUpdates}`;
  }, [auditBulkEvent, totalSelectedChanges]);

  const [auditRows, setAuditRows] = useState<IAuditTableRow[]>([]);

  useEffect(() => {
    if (!audit || !audit.isFinishedLoading) {
      return;
    }

    const processedRows = auditBulkEvent?.successfulEventIds ?? [];
    const { modifiedEvents, newEvents, deletedEvents } = audit;

    const modifiedRows: (IAuditTableRow | null)[] = modifiedEvents.map((modifiedEvent: IModifiedFlight) => {
      const importedFlight = { ...modifiedEvent.modifiedFlight, ...modifiedEvent.modifiedFields };
      const processedFoundIndex = processedRows.findIndex((val: IBulkIdsMap) => val.objectId === importedFlight._id);
      if (processedFoundIndex >= 0) {
        return null;
      }
      return {
        id: modifiedEvent.modifiedFlight._id,
        state: AuditTableState.MODIFIED,
        ocFlight: modifiedEvent.modifiedFlight,
        importedFlight,
        modifiedFields: modifiedEvent.modifiedFields,
      };
    });

    const newRows: IAuditTableRow[] = newEvents.map((newEvent: IImportFlight) => {
      const importedFlight = newEvent;
      const processedFoundIndex = processedRows.findIndex((val: IBulkIdsMap) => val.tempId === importedFlight._id);
      if (processedFoundIndex >= 0) {
        return null;
      }
      return {
        id: importedFlight._id,
        state: AuditTableState.NEW,
        importedFlight,
      };
    });

    const deletedRows: (IAuditTableRow | null)[] = deletedEvents.map((deletedEvent: any) => {
      const ocFlight = deletedEvent;
      const processedFoundIndex = processedRows.findIndex((val: IBulkIdsMap) => val.objectId === ocFlight._id);
      if (processedFoundIndex >= 0) {
        return null;
      }
      return {
        id: ocFlight._id,
        state: AuditTableState.DELETED,
        ocFlight,
      };
    });

    const rows: (IAuditTableRow | null)[] = [...modifiedRows, ...newRows, ...deletedRows].filter(
      (row: IAuditTableRow | null) => row !== null
    );
    if (Array.isArray(rows)) {
      if (!_.isEqual(rows, auditRows) && audit?.isFinishedLoading) {
        setAuditRows(rows);
      }
    }
  }, [audit, auditBulkEvent, auditRows]);

  useEffect(() => {
    if (audit?.isFinishedLoading) {
      // Communicate load status to parent import tool
      setLoadingAudit(false);
    }
  }, [audit, setLoadingAudit]);

  const handleChangeSelectedRows = useCallback(
    (rows: IAuditTableRow[], focusSelected: boolean): void => {
      const modifiedEvents: IModifiedFlight[] = [];
      const newEvents: IImportFlight[] = [];
      const deletedEvents: IImportFlight[] = [];
      const events: IImportFlight[] = [];
      const selectedRows = isNonEmptyArrayOfNumbers(rows) ? auditRows : rows;

      selectedRows.forEach((row: IAuditTableRow) => {
        switch (row.state) {
          case AuditTableState.MODIFIED:
            modifiedEvents.push({ modifiedFields: row.modifiedFields, modifiedFlight: row.ocFlight });
            events.push(row.ocFlight);
            break;
          case AuditTableState.NEW:
            newEvents.push(row.importedFlight);
            events.push(row.importedFlight);
            break;
          case AuditTableState.DELETED:
            deletedEvents.push(row.ocFlight);
            events.push(row.ocFlight);
            break;
          default:
            break;
        }
      });

      setSelectedAuditChanges({ isFinishedLoading: true, modifiedEvents, newEvents, deletedEvents });
      if (events.length < 5) {
        //just focus the timeline but do not select the flight
        dispatch(
          setFocusedEvents({
            events: events,
            shouldFocus: focusSelected ? "all" : "none",
          })
        );
      }
    },
    [auditRows, dispatch]
  );

  const discardSelectedChanges = (): void => {
    const newAudit = _.cloneDeep(audit);

    selectedAuditChanges.deletedEvents.forEach((deletedEvent) => {
      const deleteIndex = newAudit.deletedEvents.findIndex((auditDeletedEvent: IImportFlight) => {
        return deletedEvent._id === auditDeletedEvent._id;
      });
      if (deleteIndex >= 0) {
        newAudit.deletedEvents.splice(deleteIndex, 1);
      }
    });

    selectedAuditChanges.modifiedEvents.forEach((mFlight: IModifiedFlight) => {
      const index = newAudit.modifiedEvents.findIndex((auditModifiedEvent: IModifiedFlight) => {
        return mFlight.modifiedFlight._id === auditModifiedEvent.modifiedFlight._id;
      });
      if (index >= 0) {
        newAudit.modifiedEvents.splice(index, 1);
      }
    });

    selectedAuditChanges.newEvents.forEach((newEvent: IImportFlight | IGroundEvent) => {
      const index = newAudit.newEvents.findIndex((auditNewEvent: IImportFlight | IGroundEvent) => {
        const newFlight = newEvent as IFlight;
        const auditNewFlight = auditNewEvent as IFlight;
        return (
          newFlight.aircraft?.registration === auditNewFlight.aircraft?.registration &&
          newFlight.actualOut === auditNewFlight.actualOut &&
          newFlight.actualIn === auditNewFlight.actualIn &&
          newFlight.flightNumber === auditNewFlight.flightNumber &&
          newFlight.origin === auditNewFlight.origin &&
          newFlight.destination === auditNewFlight.destination
        );
      });
      if (index >= 0) {
        newAudit.newEvents.splice(index, 1);
      }
    });

    setSelectedAuditChanges(newAuditChange());
    if (auditId !== null) {
      dispatch(setAuditFromId(newAudit, auditId));
    }
  };

  const acceptSelectedChanges = useCallback(() => {
    // setIsLoading(true);
    const { modifiedEvents, newEvents, deletedEvents } = selectedAuditChanges;

    const bulkId = v1();
    setWatchingBulkId(bulkId);

    const modifiedChunks = chunkPayload<IModifiedFlight>(modifiedEvents, bulkBatchSize);
    modifiedChunks.forEach((modifiedChunk: IModifiedFlight[], index) => {
      const payload = modifiedChunk.map((mFlight: IModifiedFlight) => {
        return {
          ...mFlight.modifiedFields,
          _id: mFlight.modifiedFlight._id,
        };
      });
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.UPDATE_FLIGHT,
        batchIds: [],
        payload,
        bulkId,
        chunkId: `update-${index}`,
      };
      dispatch(bulkEvent(bulkPayload, modifiedChunk.length));
    });

    const newEventChunks = chunkPayload<IImportFlight | IGroundEvent>(newEvents, bulkBatchSize);
    newEventChunks.forEach((newEventChunk: (IImportFlight | IGroundEvent)[], index) => {
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.CREATE_FLIGHT,
        batchIds: [],
        payload: newEventChunk,
        bulkId,
        chunkId: `create-${index}`,
      };
      dispatch(bulkEvent(bulkPayload, newEventChunk.length));
    });

    const deleteEventChunks = chunkPayload<IImportFlight | IGroundEvent>(deletedEvents, bulkBatchSize);
    deleteEventChunks.forEach((deleteEventChunk: (IImportFlight | IGroundEvent)[], index) => {
      const bulkPayload: IBulkEventPayload = {
        actionType: BulkActionType.DELETE_FLIGHT,
        batchIds: deleteEventChunk.map((newEvent: IImportFlight | IGroundEvent) => newEvent._id),
        payload: null,
        bulkId,
        chunkId: `delete-${index}`,
      };
      dispatch(bulkEvent(bulkPayload, deleteEventChunk.length));
    });
  }, [dispatch, selectedAuditChanges]);

  // rows - table rows
  // flightNumber - flight number filter
  // aircraft - flight aircraft
  // origin - origin filter
  // destination - destination filter
  // start - start range
  // end - end range
  const filteredTableRows = useMemo(() => {
    const fltNumber = flightNumber ? parseInt(flightNumber) : null;
    return loadingAudit && auditRows.length === 0
      ? []
      : filterTableRows(auditRows, state, fltNumber, aircraft, origin, destination, start, end);
  }, [auditRows, state, flightNumber, aircraft, origin, destination, start, end, loadingAudit]);

  useEffect(() => {
    handleChangeSelectedRows([], false);
  }, [handleChangeSelectedRows, filteredTableRows]);

  const tableData: any = useMemo(() => {
    return [
      {
        id: "audit",
        dataType: "audit",
        label: "Audit",
        data: filteredTableRows.length ? filteredTableRows : [],
        tableInfo: auditTableInfo,
        tableInfoDisplay: {
          tableTitle: "",
          tableDescription: "",
          noMessage: loadingAudit ? "Loading audit entries..." : "No changes to internal OC data found.",
        },
      },
    ];
  }, [filteredTableRows, loadingAudit]);

  // Do not display table unless there are results from imported file to display.
  // const displayTable = tableData[0].data?.length > 0;
  const isButtonsDisabled = useMemo(() => {
    return loadingAudit || auditRows.length === 0 || bulkProcessActive;
  }, [loadingAudit, auditRows, bulkProcessActive]);

  // debounce call to change flight number filter.
  const debouncedFlightNumberChange = _.debounce((event: any) => {
    setFlightNumber(event?.target?.value ?? null);
  }, 500);

  return (
    <Grid container direction="column" sx={(theme) => AuditEventCheckStyleContainer(theme)}>
      {loadingAudit && (
        <Grid item className="loading_bar">
          <LinearProgress />
        </Grid>
      )}

      <Grid container direction="column">
        {auditBulkEvent && bulkProgress > 0 && (
          <Grid container direction="column" className="bulk_summary" style={{ marginBottom: 10 }}>
            <Grid item className="loading_bar">
              <LinearProgress variant="determinate" value={bulkProgress} />
            </Grid>
            <Grid container alignItems="center">
              <Typography variant="body2" style={{ marginLeft: 10 }}>
                {statusMessage}
              </Typography>
            </Grid>
          </Grid>
        )}

        <Grid container>
          <Button
            className="btn"
            disabled={bulkProcessActive}
            variant="contained"
            color="secondary"
            onClick={() => {
              //if you go back to load a new file clear these values
              setWatchingBulkId(null);
              setSelectedAuditChanges(newAuditChange());
              cancelAudit();
            }}
          >
            Back To Import
          </Button>
          <Button
            className="btn"
            disabled={isButtonsDisabled || totalSelectedChanges === 0}
            variant="contained"
            color="primary"
            onClick={discardSelectedChanges}
          >
            Discard Selected Changes
          </Button>
          <Button
            className="btn"
            disabled={isButtonsDisabled || totalSelectedChanges === 0}
            variant="contained"
            color="primary"
            onClick={acceptSelectedChanges}
          >
            Import Selected Changes ({totalSelectedChanges})
          </Button>
        </Grid>

        <Grid container direction="column" className="table_container">
          <Grid container className="table_column_headers">
            <Grid container className="filter_container" direction="column">
              <Grid item>
                <Typography className="flight_filter_title" variant="body2">
                  Filters apply to uploaded file column and will reset row selection.
                </Typography>
              </Grid>
              <Grid container className="flight_filter">
                <Grid item>
                  <SelectField
                    className="filter_by_state"
                    label="Event State"
                    name="auditTableRowOptions"
                    options={auditTableRowOptions}
                    value={state}
                    onChange={setState}
                    disabled={loadingAudit}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    className="flightnumber_textfield"
                    label="Flight #"
                    value={flightNumber}
                    onChange={debouncedFlightNumberChange}
                    disabled={loadingAudit}
                    //placeholder="example: 123, 200, 1220"
                  />
                </Grid>
                <Grid item className="left_pane">
                  <SelectField
                    className="aircraft_selectfield"
                    name="aircraft_selectfield"
                    label="Aircraft"
                    isMulti={false}
                    value={aircraft}
                    onChange={setAircraft}
                    options={aircraftOptions}
                    getOptionLabel={(ac: IAircraft) => `${ac.registration} - ${ac.aircraftType?.title}`}
                    getOptionValue={(aircraft: IAircraft) => aircraft._id}
                    //placeholder="Select Aircraft"
                    isSearchable={true}
                    isClearable={true}
                    disabled={loadingAudit}
                  />
                </Grid>
                <Grid item>
                  <SelectField
                    className="origin_selectfield"
                    name="origin_selectfield"
                    label="Origin"
                    value={origin}
                    onChange={setOrigin}
                    onInputChange={(ident: string) => {
                      if (ident && ident.length === 4) updateAirportOptions(ident, airportOptions, dispatch);
                    }}
                    options={Object.values(airportOptions)}
                    getOptionLabel={(option: IAirportOption) => `${option.ident} - ${option.name}`}
                    getOptionValue={(option: IAirportOption) => option.ident}
                    noOptionsMessage={() => "Search for airport name or IATA code"}
                    isSearchable={true}
                    isClearable={true}
                    disabled={loadingAudit}
                    //placeholder="example: CYHM, CYHZ, etc."
                  />
                </Grid>
                <Grid item>
                  <SelectField
                    className="destination_selectfield"
                    name="destination_selectfield"
                    label="Dest."
                    value={destination}
                    onChange={setDestination}
                    onInputChange={(ident: string) => {
                      if (ident && ident.length === 4) updateAirportOptions(ident, airportOptions, dispatch);
                    }}
                    options={Object.values(airportOptions)}
                    getOptionLabel={(option: IAirportOption) => `${option.ident} - ${option.name}`}
                    getOptionValue={(option: IAirportOption) => option.ident}
                    noOptionsMessage={() => "Search for airport name or IATA code"}
                    isSearchable={true}
                    isClearable={true}
                    disabled={loadingAudit}
                    //placeholder="example: CYHM, CYHZ, etc."
                  />
                </Grid>
                <Grid item style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                  <DateTimeField
                    className="start range_datetimefield"
                    name="event_start"
                    label={"From"}
                    value={start ?? ""}
                    // Always sets the start of this selected date (utc time)
                    onChange={(date) => (date ? setStart(moment.utc(date).startOf("day")?.valueOf()) : setStart(null))}
                    DateProps={{ maxDate: new Date(end - 86400000), isClearable: true }} // end - day.
                    isEpoch={true}
                    hideTimeControl={true}
                    maxDate={!!end && new Date(end)?.valueOf()}
                    disabled={loadingAudit}
                  />
                  <DateTimeField
                    className="end range_datetimefield"
                    name="event_end"
                    label={"Until"}
                    value={end ?? ""}
                    // Always sets the end of this selected date (utc time)
                    onChange={(date) => (date ? setEnd(moment.utc(date).endOf("day")?.valueOf()) : setEnd(null))}
                    DateProps={{ minDate: new Date(start + 86400000), isClearable: true }} // start + day.
                    isEpoch={true}
                    hideTimeControl={true}
                    minDate={!!start && new Date(start)?.valueOf()}
                    disabled={loadingAudit}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid container justifyContent="center" className="ocLoaded" style={{ marginTop: 10 }}>
              <Grid item className="column_header_title">
                Currently loaded in OC
              </Grid>
            </Grid>
            <Grid container justifyContent="center" className="fileImport" style={{ marginTop: 10 }}>
              <Grid item className="column_header_title">
                Changes uploaded from file "{fileName}"
              </Grid>
            </Grid>
          </Grid>

          <Grid item>
            <FilterSearchTable
              id={windowId}
              path={path}
              className="advanced_search_table"
              events={tableData}
              uniqueKey={"id"}
              changeSelectedRows={handleChangeSelectedRows}
              allowMultiFilter={true}
              showEventActions={true}
              hideFocusSelected={true}
              hideShowColumns={true}
              defaultRowsPerPage={25}
              maxTableWidth={1140} // locks the table at specified width.
              selectUsingCheckboxesOnly={true}
              // parentId={mosaicPanel}
              // setActiveEventModal={setActiveEventModal}
              // hideSelectionCheckboxes={true}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* <Grid item>{audit ? <p>{audit.modifiedEvents.length}</p> : "Audit not found"}</Grid> */}
    </Grid>
  );
};
